import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { selectCountryConfig } from '../../../store/country/country.selectors';
import { getCountryFeatureConfig } from '../../../hooks/use-country-config';
import { CountryConfigNames } from '../../../contexts/country-config-context/country-config-enums';
import { useIsDocumentUploadEnabled } from '../../../feature-flags/hooks/use-is-document-upload-enabled';
import {
  BATCHES_ARCHIVED_VIEW_ID,
  BATCHES_OPEN_VIEW_ID,
  BATCHES_PROCESSED_VIEW_ID,
  BATCHES_VIEW_ID,
  CONTAINER_SHIPMENTS_VIEW_ID,
  DELIVERED_VIEW_ID,
  DELIVERY_EXCEPTION_VIEW_ID,
  END_OF_DAY_CATEGORY_VIEW_ID,
  END_OF_DAY_VIEW_ID,
  FULFILLMENTS_CANCELLED_VIEW_ID,
  FULFILLMENTS_DELIVERED_VIEW_ID,
  FULFILLMENTS_DELIVERY_EXCEPTION_VIEW_ID,
  FULFILLMENTS_IN_TRANSIT_VIEW_ID,
  FULFILLMENTS_PENDING_VIEW_ID,
  FULFILLMENTS_RECENT_VIEW_ID,
  FULFILLMENTS_VIEW_ID,
  IN_TRANSIT_VIEW_ID,
  PENDING_CUSTOMS_DOCUMENTS_VIEW_ID,
  RECENT_VIEW_ID,
  REFUND_ASSIST_VIEW_ID,
  RETURNS_VIEW_ID,
  SCHEDULED_PICKUPS_VIEW_ID,
  SHIPMENT_PICKUPS_VIEW_ID,
  SHIPPED_VIEW_ID,
  USPS_PICKUP_VIEW_ID,
  VOIDED_VIEW_ID,
} from '../../../components/shipping-grid/views/view-id-constants';
import { useLocation } from 'react-router-dom';

const ShippingLeftNavContext = React.createContext({});

const defaultViews = [
  {
    id: SHIPPED_VIEW_ID,
    items: [
      { id: RECENT_VIEW_ID, theme: 'light' },
      { id: IN_TRANSIT_VIEW_ID, theme: 'light' },
      { id: DELIVERED_VIEW_ID, theme: 'light' },
      { id: DELIVERY_EXCEPTION_VIEW_ID, theme: 'light' },
      { id: VOIDED_VIEW_ID, theme: 'light' },
      { id: REFUND_ASSIST_VIEW_ID, theme: 'light' },
      { id: PENDING_CUSTOMS_DOCUMENTS_VIEW_ID, theme: 'light' },
    ],
  },
  {
    id: FULFILLMENTS_VIEW_ID,
    items: [
      { id: FULFILLMENTS_RECENT_VIEW_ID, theme: 'light' },
      { id: FULFILLMENTS_PENDING_VIEW_ID, theme: 'light' },
      { id: FULFILLMENTS_IN_TRANSIT_VIEW_ID, theme: 'light' },
      { id: FULFILLMENTS_DELIVERED_VIEW_ID, theme: 'light' },
      { id: FULFILLMENTS_DELIVERY_EXCEPTION_VIEW_ID, theme: 'light' },
      { id: FULFILLMENTS_CANCELLED_VIEW_ID, theme: 'light' },
    ],
  },
  { id: RETURNS_VIEW_ID },
  {
    id: END_OF_DAY_CATEGORY_VIEW_ID,
    items: [
      { id: END_OF_DAY_VIEW_ID, theme: 'light' },
      { id: CONTAINER_SHIPMENTS_VIEW_ID, theme: 'light' },
    ],
  },
  {
    id: SCHEDULED_PICKUPS_VIEW_ID,
    items: [{ id: SHIPMENT_PICKUPS_VIEW_ID, theme: 'light' }],
  },
  {
    id: BATCHES_VIEW_ID,
    items: [
      { id: BATCHES_OPEN_VIEW_ID, theme: 'light' },
      { id: BATCHES_PROCESSED_VIEW_ID, theme: 'light' },
      { id: BATCHES_ARCHIVED_VIEW_ID, theme: 'light' },
    ],
  },
];

const getAllViews = (showManualUSPSPickup, isDocumentUploadEnabled = false) => {
  let views = JSON.parse(JSON.stringify(defaultViews));

  if (!isDocumentUploadEnabled) {
    views = views.map((item) => {
      if (item.id !== SHIPPED_VIEW_ID || !item.items) return item;
      return {
        ...item,
        items: item.items.filter((subItem) => subItem.id !== PENDING_CUSTOMS_DOCUMENTS_VIEW_ID),
      };
    });
  }

  if (showManualUSPSPickup) {
    return views.map((item) => {
      if (item.id !== SCHEDULED_PICKUPS_VIEW_ID) {
        return item;
      }
      item.items.push({ id: USPS_PICKUP_VIEW_ID, theme: 'light' });
      return item;
    });
  }
  return views.map((item) => {
    if (item.id !== SCHEDULED_PICKUPS_VIEW_ID) {
      return item;
    }
    return {
      ...item,
      items: item.items.filter((subItem) => subItem.id !== USPS_PICKUP_VIEW_ID),
    };
  });
};

export const getSelectedViewFromPathName = (pathname = '') => {
  const allViews = getAllViews(true, true); // include all views for URL path matching
  let selectedItem;
  allViews.forEach((item) => {
    if (selectedItem) {
      return;
    }
    if (!item.items) {
      if (pathname.includes(item.id)) {
        selectedItem = item;
      }
      return;
    }
    item.items.forEach((subItem) => {
      let pathId = subItem.id;
      if (pathname.includes(item.id) && pathId.includes(`${item.id}-`)) {
        pathId = pathId.replace(`${item.id}-`, '');
      }
      if (pathname.includes(pathId)) {
        selectedItem = subItem;
      }
    });
    if (selectedItem) {
      return;
    }
    if (pathname.includes(item.id)) {
      selectedItem = item;
    }
  });
  return selectedItem;
};

export const useSelectedShippingViewId = () => {
  const { pathname } = useLocation();
  return getSelectedViewFromPathName(pathname)?.id;
};

const ShippingLeftNavContextProvider = ({ children }) => {
  const countryConfig = useSelector(selectCountryConfig);
  const [showManualUSPSPickup] = getCountryFeatureConfig(countryConfig, CountryConfigNames.ManualUSPSPickup);
  const isDocumentUploadEnabled = useIsDocumentUploadEnabled();
  const selectedViewId = useSelectedShippingViewId();

  const shipmentsGridViews = useMemo(() => {
    const allViews = getAllViews(showManualUSPSPickup, isDocumentUploadEnabled);
    return allViews.map((view) => {
      if (view.id === selectedViewId) {
        return {
          ...view,
          selected: true,
          expanded: !!view.items,
        };
      }
      if (view.items) {
        const hasSubItemSelected = view.items.some((item) => item.id === selectedViewId);
        if (!hasSubItemSelected) {
          return view;
        }
        return {
          ...view,
          expanded: true,
          items: view.items.map((item) => {
            if (item.id !== selectedViewId) {
              return item;
            }
            return {
              ...item,
              selected: true,
            };
          }),
        };
      }
      return view;
    });
  }, [selectedViewId, showManualUSPSPickup, isDocumentUploadEnabled]);

  const contextValue = useMemo(
    () => ({
      shipmentsGridViews,
    }),
    [shipmentsGridViews],
  );

  return <ShippingLeftNavContext.Provider value={contextValue}>{children}</ShippingLeftNavContext.Provider>;
};

ShippingLeftNavContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export { ShippingLeftNavContext, ShippingLeftNavContextProvider };
